import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_STYLES = {
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  "On Trip": "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800",
  Maintenance: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  Created: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  Loading: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400 dark:border-indigo-800",
  "In-Transit": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  Delivered: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  rejected: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800",
  Blacklisted: "bg-rose-600 text-white border-rose-600 dark:bg-rose-600 dark:text-white dark:border-rose-600",
  Left: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
  Disabled: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800",
};

export const STATUS_LABEL = { "In-Transit": "Delivery", Delivered: "Loading Point" };
export const statusLabel = (s) => STATUS_LABEL[s] || s;

export function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[status] || STATUS_STYLES.Created}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {statusLabel(status)}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, accent = "text-primary", testid, delay = 0 }) {
  return (
    <div data-testid={testid} className="card-base p-5 fade-up transition-colors hover:border-primary/40" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start justify-between">
        <span className="label-xs">{label}</span>
        {Icon && <span className={`rounded-lg bg-accent p-2 ${accent}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <div className="mt-3 text-2xl font-semibold amount tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9 bg-card" />
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function Empty({ text = "No records found", testid }) {
  return <div data-testid={testid} className="py-14 text-center text-sm text-muted-foreground">{text}</div>;
}

export function TableWrap({ children, scroll = true, testid }) {
  return (
    <div className="card-base overflow-hidden">
      <div data-testid={testid} className={scroll ? "max-h-[55vh] overflow-auto md:max-h-[65vh] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-secondary" : "overflow-x-auto"}>
        <table className="w-full text-sm">{children}</table>
      </div>
    </div>
  );
}

export const th = "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-secondary/60 whitespace-nowrap";
export const td = "px-4 py-3 whitespace-nowrap";
export const tr = "border-t transition-colors hover:bg-secondary/50";
